/* eslint-disable @typescript-eslint/ban-ts-comment */
import axios, { AxiosPromise, AxiosResponse } from 'axios'
import thumbnail from '@bt-react/libs/thumbnail'
import getEnvironment, { EnvironmentObject, Environment } from '@bt-react/libs/getEnvironment'
import has from 'lodash/has'
import {
  DialInCodeFetchSuccessResponse,
  DialInPhoneNumbersFetchSuccessResponse,
  ApiGatewayInterface,
  FetchTokenOptionsInterface,
  PlayInListResponse,
  CommunicationResponse,
  CommunicationRequest,
  UnmarshalResponse,
  CommunicationType,
  VotesReturn,
  VotesRequest,
  VoteReturn,
  StartVoteRequest
} from './apiGatewayTypes'
import { environment } from '../../../environments/environment'
import { Vote } from '@bt-react/components/Votes'
import parseISO from 'date-fns/parseISO'
import utcToZonedTime from 'date-fns-tz/utcToZonedTime'
import format from 'date-fns/format'
import { AuditEntry, addEntry } from '../../lib/audit'
import { VirtualBackgrounds } from '../../components/VirtualBackgroundPicker/VirtualBackgroundPicker'
import { QuestionsDocValue } from '../../dataStore/actions/questions/questionsTypes'

class ApiGateway implements ApiGatewayInterface {

  _environment: EnvironmentObject = getEnvironment(true)
  proxyPath = `http://localhost:3000`
  mockPath = `http://localhost:4200`
  basePath = `https://talks.${this._environment.domain}`
  // path for general BT APIs, not talk specific
  btBasePath = `https://www.${this._environment.domain}`
  talksProxy =  `http://localhost:4200`
  private getExtraHeaders = (options: FetchTokenOptionsInterface) => {
    let extraHeaders

    if (has(options, 'extraHeaders')) {

      extraHeaders = options.extraHeaders

    }

    if (has(environment, 'header')) {

      extraHeaders = environment.header

    }

    const split = extraHeaders ? extraHeaders.split(':') : []

    if (split.length > 0) {
      return { [split[0].toLowerCase()]: split[1].toLowerCase() }
    }
    return
  }
  getBasePathOrTalksProxy = () => this._environment.env === Environment.localhost ? this.talksProxy : this.basePath
  getBasePath = () => this._environment.env === Environment.localhost ? this.mockPath : this.basePath
  getBTPathOrProxy = () => this._environment.env === Environment.localhost ? this.proxyPath : this.btBasePath
  getBTBasePath = () => this._environment.env === Environment.localhost ? this.mockPath : this.btBasePath
  goLive = (id: string) => axios.post(`${this.basePath}/director/ui-auth/${id}/start`, null, { withCredentials: true })
  endLive = (id: string) => axios.post(`${this.basePath}/director/ui-auth/${id}/stop`, null, { withCredentials: true })

  /**
   * The function to generate and retrieve a participant dial-in code
   * @prop id - Twilio Roomname
   * @prop presenterName - The user's full name
   * @prop externalUserId - The BrightTALK external ID V2
   */
  fetchDialInOptions = (id: string, presenterName: string, externalUserId: string): AxiosPromise<DialInCodeFetchSuccessResponse> =>
    axios.post(`${this.getBasePath()}/twiliovoice/ui-auth/${id}`, {
    presenterName,
    externalUserId
  }, { withCredentials: true })

  fetchDialInNumbers = (id: string): AxiosPromise<DialInPhoneNumbersFetchSuccessResponse> =>
    axios.get(`${this.getBasePath()}/twiliovoice/ui-auth/${id}/phone-numbers`, { withCredentials: true })

  getPresenterToAudienceAnnouncement = (id: string): AxiosPromise =>
    axios.get(`${this.getBasePath()}/audiencemessaging/ui-auth/${id}/announce`, { withCredentials: true })

  sendPresenterToAudienceAnnouncement = (id: string, message: string): AxiosPromise =>
    axios.post(`${this.getBasePath()}/audiencemessaging/ui-auth/${id}/announce`, {
    message
  }, { withCredentials: true })

  clearPresenterToAudienceAnnouncement = (id: string): AxiosPromise =>
    axios.delete(`${this.getBasePath()}/audiencemessaging/ui-auth/${id}/announce`, { withCredentials: true })

  fetchToken (id: string, tokenName: string, options: FetchTokenOptionsInterface = {}) {

    const headers = this.getExtraHeaders(options)

    if (headers) {
      return axios.get(`${this.getBasePath()}/greenroom/host-auth/${id}/twilio-token/${tokenName}`, {
        headers,
        /*@ts-ignore*/
        retry: 3,
        retryDelay: 1000
      })

    }
    else {

      return axios.get(`${this.getBasePath()}/greenroom/ui-auth/${id}/twilio-token/${tokenName}`, { withCredentials: true })

    }

  }

  getPlayInList = (id: string): AxiosPromise<PlayInListResponse> =>
    axios.get(`${this.getBasePath()}/playin/ui-auth/${id}/vod`, { withCredentials: true })
  addPlayInToList = (id: string, videoId: number): AxiosPromise<PlayInListResponse> =>
    axios.post(`${this.getBasePath()}/playin/ui-auth/${id}/${videoId}/vod`, null, { withCredentials: true })
  removePlayInFromList = (id: string, videoId: number): AxiosPromise<PlayInListResponse> =>
    axios.delete(`${this.getBasePath()}/playin/ui-auth/${id}/${videoId}/vod`, { withCredentials: true })
  fetchCommunications = async (data: CommunicationRequest, timeZone: string): Promise<UnmarshalResponse> => {

    const { getThumbnailImage } = thumbnail

    const formatCloseTime = (closeTime: string, timeZone: string): string => {

      const parsedDatetime = parseISO(closeTime)
      const _closeTime = timeZone
        ? utcToZonedTime(parsedDatetime, timeZone)
        : parsedDatetime

      return format(_closeTime, 'd/M/Y')

    }

    const unMarshalCommunications = (communications: CommunicationResponse['communications'], timeZone: string): CommunicationType[] =>
      communications.map((comm) => {

        const sources: CommunicationType['sources'] = (comm?.provider?.renditions ?? [])
          .filter(rendition => !!rendition.isActive)
          .map(rendition => ({
            src: rendition.url,
            type: rendition.container
          }))

        const m3u8Rendition = (comm?.provider?.renditions ?? [])
          .find(({ url, isActive }) => url.indexOf('.m3u8') !== -1 && isActive)?.url ?? ''

        return {
          id: comm.id,
          title: comm.title,
          duration: parseInt(comm.duration, 10),
          description: comm.description,
          presenter: comm.presenter,
          closeTimeFormatted: formatCloseTime(comm.closeTime, timeZone),
          previewImage: getThumbnailImage(comm),
          url: m3u8Rendition,
          sources
        }

      })

    const unmarshalResponse = (response: CommunicationResponse, timeZone: string): UnmarshalResponse => ({
      total: response.total,
      communications: unMarshalCommunications(response.communications, timeZone)
    })

    let response: AxiosResponse<CommunicationResponse>

    try {

      response = await axios.post<CommunicationResponse>(
        `${this.getBTBasePath()}/service/communication/v1/communications`,
        data,
        { withCredentials: true }
      )

    }
    catch (e) {

      addEntry('PresenterService', 'FetchCommunicationError', `Error fetching communication: ${e}.`)

    }

    // @ts-ignore
    return unmarshalResponse(response.data, timeZone)

  }

  fetchVote = (id: number): AxiosPromise<VoteReturn> =>
    axios.get(`${this.getBTBasePath()}/service/audience/vote/${id}`, { withCredentials: true })
  createVote = (data: VotesRequest): AxiosPromise<VoteReturn> =>
    axios.post(`${this.getBTBasePath()}/service/audience/vote`, data, { withCredentials: true })
  deleteVote = (id: number): Promise<AxiosResponse<void>> =>
    axios.delete(`${this.getBTBasePath()}/service/audience/vote/${id}`, { withCredentials: true })
  updateVote = ( data: Vote | StartVoteRequest): Promise<AxiosResponse<void>> =>
    axios.put(`${this.getBTBasePath()}/service/audience/vote/${data.id}`, data, { withCredentials: true })

  getVotes = (id:string): AxiosPromise<VotesReturn> =>
    axios.get(`${this.getBasePath()}/audiencemessaging/ui-auth/${id}/votelist`, { withCredentials: true })
  getVotesSummaries = (id:string): AxiosPromise<VotesReturn> =>
    axios.get(`${this.getBasePath()}/audiencemessaging/ui-auth/${id}/votesummaries`, { withCredentials: true })

  getQuestionsDataAndToken = (channelId: number, webcastId: number, externalId: string): AxiosPromise<string> =>
    axios.post(`${this.basePath}/conversation/ui-auth/${channelId}-${webcastId}/conversation`, { externalUserIdv2: externalId }, { withCredentials: true })

  getQuestionsDoc = (channelId: number, webcastId: number): AxiosPromise<QuestionsDocValue> =>
    axios.get(`${this.basePath}/conversation/ui-auth/${channelId}-${webcastId}/conversationstate`, { withCredentials: true })

  setQuestionAnswered = (channelId: number, webcastId: number, conversationId: string, trueOrFalse: boolean) =>
    axios.post(`${this.basePath}/conversation/ui-auth/${channelId}-${webcastId}/answered/${conversationId}/${trueOrFalse}`, null, { withCredentials: true })

  setQuestionPriority = (channelId: number, webcastId: number, conversationId: string, trueOrFalse: boolean) =>
    axios.post(`${this.basePath}/conversation/ui-auth/${channelId}-${webcastId}/priority/${conversationId}/${trueOrFalse}`, null, { withCredentials: true })




  // audit
  addAuditEntry = (roomname: string, record: AuditEntry, options: FetchTokenOptionsInterface = {}) => {

    const headers = this.getExtraHeaders(options)
    if (headers) {

      return axios.post(`${this.getBasePath()}/error/host-auth/${roomname}/auditlog`, record, { headers })

    }
    return axios.post(`${this.getBasePath()}/error/ui-auth/${roomname}/auditlog`, record, { withCredentials: true })

  }


  // stream ingest
  getEventStreamDetails = (roomname: string): AxiosPromise => axios.get(`${this.getBasePath()}/resource/ui-auth/${roomname}`, { withCredentials: true })

  fetchUserSettings = (): AxiosPromise => axios.get(`${this.getBasePath()}/usersettings/user-auth/getuserstate`, { withCredentials: true })

  // extend user session
  extendUserSession = (): AxiosPromise => axios(`${this.getBTBasePath()}/service/user/v1/session/lastseen`, {
    method: 'POST',
    headers: {
      'BrightTALK-API-Client': 'brighttalk-talks'
    },
    withCredentials: true
  })

  setUserBackground = (backgroundType: VirtualBackgrounds): AxiosPromise => axios.post(`${this.getBasePath()}/usersettings/user-auth/setbackgroundpreference`, {currentBackground: backgroundType} ,{ withCredentials: true })


  uploadVirtualBackgroundImage = (file: File): AxiosPromise => {
    const formData = new FormData()
    formData.append('file', file)
    return axios.post('/service/image-service/user/virtual-background', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  getAvailableFeatures = (channelId: number): AxiosPromise => axios.get(`${this.getBasePath()}/director/user-auth/${channelId}/enabledfeatures`, { withCredentials: true})

  getCheckAdminRights = (): AxiosPromise => axios.get(`${this.basePath}/director/admin-auth/check`, { withCredentials: true })

  markSlidesAsFailed = (talkId: string, slidesId: string): AxiosPromise => axios.put(`${this.getBasePath()}/twilio/restricted/admin-auth/${talkId}/slides/${slidesId}/failed`, { errorMessage: "Set to failed manually by BrightTALK staff" }, { withCredentials: true })

  getCanUserPresent = (id: string) => axios.get(`${this.getBTBasePath()}/service/communication/v1/present/communication/${id}`, { withCredentials: true })

  getCheckUserHasRoles = () => axios.get(`${this.getBTBasePath()}/service/user/session/user`, { withCredentials: true })
}

export default new ApiGateway()
